import type { ReactNode } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'

import LayoutContainer from 'components/Common/LayoutContainer'
import Carousel from 'LuxKit/Carousel/Carousel'
import Image from 'components/Common/Image'
import Clickable from 'components/Common/Clickable'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import { fillArray } from 'lib/array/arrayUtils'

import ExperiencesHomepageBrowseTileSkeleton from './ExperiencesHomepageBrowseTileSkeleton'

const messages = defineMessages({
  cardOffers: {
    id: 'evJDufufhLSBVehUXwU4',
    defaultMessage: '{count, plural, one {# offer} other {# offers}}',
    description: 'Offer count caption under a category tile title on the Experiences homepage uplift',
  },
  cardAriaLabel: {
    id: 'jCtSkncEyWKW5vLaWbom',
    defaultMessage: '{title}. {offersLabel}',
    description: 'Accessibility label for a browse trend tile link combining title and offer count on Experiences homepage uplift',
  },
})

export type BrowseTile = {
  key: string
  title: string
  offerCount?: number
  image: { id: string, title?: string }
  to: string
}

type Props = {
  title: ReactNode
  actionLabel?: string
  actionUrl?: string
  tiles?: ReadonlyArray<BrowseTile>
  isLoading?: boolean
  skeletonCount?: number
}

const Title = styled(Heading)`
  flex: 1 1 auto;
  min-inline-size: 0;
  text-align: start;
`

const BrowseTileCardLink = styled(Clickable)`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  block-size: ${rem(112)};
  padding: ${rem(16)};
  overflow: hidden;
  border-radius: ${props => props.theme.borderRadius.L};
  color: inherit;
  text-decoration: none;
`

const BrowseTileImage = styled.div`
  position: absolute;
  inset: 0;

  > picture,
  > img {
    display: block;
    inline-size: 100%;
    block-size: 100%;
  }
`

const BrowseTileGradient = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: linear-gradient(0deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0.12) 100%);
`

const BrowseTileCopy = styled(Group)`
  position: relative;
  z-index: 1;
  align-items: flex-start;
  inline-size: 100%;
  min-inline-size: 0;
`

const BrowseTileOfferCount = styled(Caption)`
  &.hidden {
    visibility: hidden;
  }
`

function ExperiencesHomepageBrowseTileCarousel({
  title,
  actionLabel,
  actionUrl,
  tiles = [],
  isLoading = false,
  skeletonCount = 6,
}: Props) {
  const intl = useIntl()

  return (
    <LayoutContainer>
      <Group direction="vertical" gap={16}>
        <Group
          direction="horizontal"
          verticalAlign="center"
          gap={20}
          tabletGap={16}
          tabletHorizontalAlign="space-between"
          fullWidth
        >
          <Title variant="heading3">
            {title}
          </Title>
          {isLoading ? (
            <>
              <CSSBreakpoint max="mobile">
                <TextLoadingBox typography="body-small" width={96} />
              </CSSBreakpoint>
              <CSSBreakpoint min="tablet">
                <TextLoadingBox typography="body-medium" width={112} />
              </CSSBreakpoint>
            </>
          ) : (
            <>
              <CSSBreakpoint max="mobile">
                <TextButton
                  kind="tertiary"
                  size="small"
                  horizontalOutdent="end"
                  to={actionUrl}
                  nonInteractable={!actionUrl}
                >
                  {actionLabel}
                </TextButton>
              </CSSBreakpoint>
              <CSSBreakpoint min="tablet">
                <TextButton
                  kind="tertiary"
                  size="medium"
                  horizontalOutdent="end"
                  to={actionUrl}
                  nonInteractable={!actionUrl}
                >
                  {actionLabel}
                </TextButton>
              </CSSBreakpoint>
            </>
          )}
        </Group>
        <Carousel
          width={rem(180)}
          gap={12}
          snap="start"
        >
          {isLoading && fillArray(skeletonCount).map((id) => (
            <ExperiencesHomepageBrowseTileSkeleton key={id} />
          ))}
          {!isLoading && tiles.map((tile) => {
            const ariaLabel = tile.offerCount !== undefined
              ? intl.formatMessage(messages.cardAriaLabel, {
                  title: tile.title,
                  offersLabel: intl.formatMessage(messages.cardOffers, { count: tile.offerCount }),
                })
              : tile.title
            return (
              <BrowseTileCardLink
                key={tile.key}
                to={tile.to}
                aria-label={ariaLabel}
              >
                <BrowseTileImage>
                  <Image
                    fit="center"
                    dpr={2}
                    width={180}
                    height={112}
                    image={{ id: tile.image.id, title: tile.image.title ?? tile.title }}
                  />
                </BrowseTileImage>
                <BrowseTileGradient />
                <BrowseTileCopy direction="vertical">
                  <CSSBreakpoint max="mobile">
                    <BodyText variant="medium" weight="bold" colour="dark-contrast">
                      {tile.title}
                    </BodyText>
                    <BrowseTileOfferCount
                      variant="medium"
                      colour="dark-contrast"
                      className={clsx({ hidden: tile.offerCount === undefined })}
                      aria-hidden={tile.offerCount === undefined}
                    >
                      {intl.formatMessage(messages.cardOffers, { count: tile.offerCount ?? 0 })}
                    </BrowseTileOfferCount>
                  </CSSBreakpoint>
                  <CSSBreakpoint min="tablet">
                    <BodyText variant="large" weight="bold" colour="dark-contrast">
                      {tile.title}
                    </BodyText>
                    <BrowseTileOfferCount
                      variant="large"
                      colour="dark-contrast"
                      className={clsx({ hidden: tile.offerCount === undefined })}
                      aria-hidden={tile.offerCount === undefined}
                    >
                      {intl.formatMessage(messages.cardOffers, { count: tile.offerCount ?? 0 })}
                    </BrowseTileOfferCount>
                  </CSSBreakpoint>
                </BrowseTileCopy>
              </BrowseTileCardLink>
            )
          })}
        </Carousel>
      </Group>
    </LayoutContainer>
  )
}

export default ExperiencesHomepageBrowseTileCarousel
